import {
  GoogledLyricsDetailsView,
  GoogledLyricsSourceLocation,
  useGetLyricsSource,
} from "../api-generated";
import SourceHeader from "./SourceHeader";
import Blob from "./Blob";
import { formatTimestamp, useSourceUrlDetails } from "../utils";
import CenteredLoading from "./Loading";
import ErrorLoading from "./ErrorLoading";
import { getSourceQueryKey } from "../domain_data";
import { useCallback, useMemo } from "react";
import useMultiSelect, { MultiSelectableBlob } from "../hooks/useMultiSelect";
import clsx from "clsx";
import MultiSelectMenu from "./MultiSelectMenu";
import SpotifyEmbed, { useSpotifyEmbed } from "./SpotifyEmbed";
import {
  faPlay,
  faPause,
  faForward,
  faBackward,
  faBackwardStep,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useKeyPressEvent } from "../hooks";

const GoogledLyricsSource = ({
  source,
}: {
  source: GoogledLyricsDetailsView;
}) => {
  const location: GoogledLyricsSourceLocation = {
    type: "googled_lyrics",
    sourceId: source.id,
  };

  const {
    isPaused,
    isReady,
    duration,
    position,
    onControllerReady,
    seekBackward,
    seekToBeginning,
    seekForward,
    togglePlayPause,
  } = useSpotifyEmbed();

  const getEmptyMultiSelectState = useCallback(
    (): number[][][] => [Array(source.lines.length).fill([])],
    [JSON.stringify(source.lines)]
  );

  const multiSelectableBlobGroups: MultiSelectableBlob[][] = useMemo(
    () => [source.lines.map((blob) => blob.tokens)],
    [JSON.stringify(source.lines)]
  );

  const {
    multiSelectedWords,
    multiSelectTokenIndeces,
    setIsMultiSelectOn,
    closeMultiSelect,
    isMultiSelectOn,
    handleToggleMultiSelect,
    removeWordFromMultiSelect,
  } = useMultiSelect(multiSelectableBlobGroups, getEmptyMultiSelectState);

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "S") {
        seekBackward();
      } else if (event.key === "<") {
        seekBackward();
      } else if (event.key === ">") {
        seekForward();
      } else if (event.key === " ") {
        togglePlayPause();
        event.preventDefault();
      }
    },
    [seekBackward, seekForward, togglePlayPause]
  );

  useKeyPressEvent(
    {
      id: "googled-lyrics-source",
      priority: 0,
      useCallbackGeneratedCB: handleKeyEvent,
    },
    true
  );

  return (
    <>
      <SourceHeader
        sourceName={source.name}
        sourceType="googled_lyrics"
        sourceActions={[
          {
            label: "Go to track in Spotify",
            href: `https://open.spotify.com/track/${source.externalId}?si=0a67bd29f0ca478a`,
          },
        ]}
      />

      <div>
        <SpotifyEmbed
          trackId={source.externalId}
          onControllerReady={onControllerReady}
        />

        {isReady && (
          <div id="spotify-embed-control-panel" className="flex gap-2">
            <div>
              <button onClick={seekToBeginning} className="p-4">
                <FontAwesomeIcon icon={faBackwardStep} />
              </button>
            </div>

            <div>
              <button onClick={seekBackward} className="p-4">
                <FontAwesomeIcon icon={faBackward} />
              </button>
            </div>

            <div>
              <button onClick={togglePlayPause} className="p-4">
                <FontAwesomeIcon icon={isPaused ? faPlay : faPause} />
              </button>
            </div>

            <div>
              <button onClick={seekForward} className="p-4">
                <FontAwesomeIcon icon={faForward} />
              </button>
            </div>

            {duration !== undefined && position !== undefined && (
              <div className="p-4">
                {formatTimestamp(position)} / {formatTimestamp(duration)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between gap-4 pb-4">
        <div>
          <button
            className={clsx([
              "font-bold font-xs text-white bg-blue-500 rounded active:bg-blue-800 align-middle py-1 px-4",
            ])}
            onClick={
              isMultiSelectOn
                ? closeMultiSelect
                : () => setIsMultiSelectOn(true)
            }
          >
            MS
          </button>
          <div>
            {isMultiSelectOn && (
              <MultiSelectMenu
                wordsSelected={multiSelectedWords}
                onClickRemove={removeWordFromMultiSelect}
                onCloseMenu={closeMultiSelect}
                location={location}
              />
            )}
          </div>
        </div>
      </div>
      {source.lines.map((blob, lineIndex) => (
        <Blob
          key={lineIndex}
          blob={blob}
          location={{ ...location, lineIndex } as GoogledLyricsSourceLocation}
          tokensInMultiSelect={multiSelectTokenIndeces[0][lineIndex]}
          onTokenClick={(_, tokenIndex) =>
            handleToggleMultiSelect({
              blobGroupIndex: 0,
              blobIndex: lineIndex,
              tokenIndex,
            })
          }
          onlyFocusOnHover
        />
      ))}
    </>
  );
};

const Wrapped: React.FC = () => {
  const { sourceId } = useSourceUrlDetails();
  const queryKey = getSourceQueryKey(sourceId!);
  const {
    data: source,
    isLoading,
    isError,
    error,
  } = useGetLyricsSource(sourceId!, {
    query: {
      queryKey,
      keepPreviousData: false,
    },
  });

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (isError) {
    return <ErrorLoading error={error} />;
  }

  return <GoogledLyricsSource source={source} />;
};

export default Wrapped;
